import api from '../api';

function data<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

export interface CategoryExpertAssignment {
  id: string;
  eventId: string | null;
  regionalEventId: string | null;
  categorySlug: string;
  userId: string;
  validFrom: string | null;
  validUntil: string | null;
  maxMeetings: number | null;
  manualSchedulingUrl: string | null;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    calendlySchedulingUrl?: string | null;
    calendlyWebhooksEnabled?: boolean;
  };
}

// cccd-event-scoped-experts-and-availability: la asignación queda scoped a
// UN evento o regional específico — nunca un categorySlug suelto que cruce
// ediciones.
export const categoryExpertsAdmin = {
  list: (params: { eventId?: string; regionalEventId?: string; categorySlug?: string }) =>
    api.get<{ data: CategoryExpertAssignment[] }>('/cccd/admin/category-experts', { params }).then(data),

  create: (body: { eventId?: string; regionalEventId?: string; categorySlug: string; userId: string; validFrom?: string | null; validUntil?: string | null; maxMeetings?: number | null; manualSchedulingUrl?: string | null }) =>
    api.post<{ data: CategoryExpertAssignment }>('/cccd/admin/category-experts', body).then(data),

  update: (id: string, body: Partial<{ validFrom: string | null; validUntil: string | null; maxMeetings: number | null; manualSchedulingUrl: string | null }>) =>
    api.patch<{ data: CategoryExpertAssignment }>(`/cccd/admin/category-experts/${id}`, body).then(data),

  remove: (id: string) =>
    api.delete(`/cccd/admin/category-experts/${id}`),
};

export interface MeetWindow {
  id: string;
  eventId: string | null;
  regionalEventId: string | null;
  label: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface MeetAvailabilityEntry {
  id: string;
  categoryExpertId: string;
  meetWindowId: string;
  date: string;
  startTime: string;
  endTime: string;
}

export interface MeetLinkedExpert {
  id: string;
  meetWindowId: string;
  categoryExpertId: string;
  createdAt: string;
  categoryExpert: { id: string; categorySlug: string; user: { id: string; name: string; email: string } };
}

export interface MeetCoverage {
  meetWindow: MeetWindow;
  availability: (MeetAvailabilityEntry & {
    categoryExpert: { id: string; categorySlug: string; user: { id: string; name: string; email: string } };
  })[];
  linkedExperts: MeetLinkedExpert[];
}

export const meetWindowsAdmin = {
  list: (params: { eventId?: string; regionalEventId?: string }) =>
    api.get<{ data: MeetWindow[] }>('/cccd/admin/event-meet-windows', { params }).then(data),

  create: (body: { eventId?: string; regionalEventId?: string; label: string; startDate: string; endDate: string }) =>
    api.post<{ data: MeetWindow }>('/cccd/admin/event-meet-windows', body).then(data),

  update: (id: string, body: Partial<{ label: string; startDate: string; endDate: string }>) =>
    api.patch<{ data: MeetWindow }>(`/cccd/admin/event-meet-windows/${id}`, body).then(data),

  remove: (id: string) =>
    api.delete(`/cccd/admin/event-meet-windows/${id}`),

  coverage: (id: string) =>
    api.get<{ data: MeetCoverage }>(`/cccd/admin/event-meet-windows/${id}/coverage`).then(data),

  // Solo un experto vinculado explícitamente por el admin a ESTE meet puede
  // ver/declarar disponibilidad ahí — tener una asignación de categoría en
  // el mismo evento no basta.
  linkableExperts: (id: string) =>
    api.get<{ data: MeetLinkedExpert['categoryExpert'][] }>(`/cccd/admin/event-meet-windows/${id}/linkable-experts`).then(data),

  linkExpert: (id: string, categoryExpertId: string) =>
    api.post<{ data: MeetLinkedExpert }>(`/cccd/admin/event-meet-windows/${id}/experts`, { categoryExpertId }).then(data),

  unlinkExpert: (id: string, categoryExpertId: string) =>
    api.delete(`/cccd/admin/event-meet-windows/${id}/experts/${categoryExpertId}`),
};

// Self-service — el propio experto declara su disponibilidad, nunca un admin.
export const myMeetAvailability = {
  listMeets: () =>
    api.get<{ data: { meets: (MeetWindow & { categoryExpertId: string; availability: MeetAvailabilityEntry[] })[] } }>('/me/meet-availability/meets').then(data),

  declare: (body: { meetWindowId: string; date: string; startTime: string; endTime: string }) =>
    api.post<{ data: MeetAvailabilityEntry }>('/me/meet-availability', body).then(data),

  remove: (id: string) =>
    api.delete(`/me/meet-availability/${id}`),
};

export interface CalendlyEventTypeOption {
  uri: string;
  name: string;
  schedulingUrl: string;
  active: boolean;
}

export interface CalendlyConnectionStatus {
  connected: boolean;
  eventTypeUri: string | null;
  schedulingUrl: string | null;
  webhooksEnabled: boolean;
}

// Self-service — solo actúa sobre la cuenta del usuario autenticado (el PAT
// es un secreto personal, ver calendly-me.ts en la API).
export const myCalendly = {
  getStatus: () =>
    api.get<{ data: CalendlyConnectionStatus }>('/me/calendly').then(data),

  connect: (personalAccessToken: string) =>
    api.post<{ data: { eventTypes: CalendlyEventTypeOption[] } }>('/me/calendly/connect', { personalAccessToken }).then(data),

  selectEventType: (eventTypeUri: string) =>
    api.patch<{ data: { eventTypeUri: string; schedulingUrl: string; webhooksEnabled: boolean } }>('/me/calendly/event-type', { eventTypeUri }).then(data),

  disconnect: () =>
    api.delete('/me/calendly'),
};
